import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { subscriptionAPI } from '../lib/api';
import { toast } from 'sonner';

const TEAL = '#00a99d';

const plans = [
  { tier: 'BASIC', name: 'Basic', price: 29, desc: 'Perfect for getting started with a single property.', features: ['1 property', 'Up to 2 units', 'Online rent collection', 'Tenant portal', 'Basic reporting', 'Email support'] },
  { tier: 'PRO', name: 'Pro', price: 59, popular: true, desc: 'Great for landlords managing one property with more units.', features: ['1 property', 'Up to 5 units', 'Online rent collection', 'Tenant portal', 'Advanced analytics', 'Priority support'] },
  { tier: 'EXPERT', name: 'Expert', price: 99, desc: 'For portfolio landlords with multiple properties.', features: ['Up to 5 properties', 'Up to 20 units', 'All Pro features', 'Custom branding', 'Lease automation', 'Audit logs'] },
  { tier: 'ENTERPRISE', name: 'Enterprise', price: 299, desc: 'Unlimited scale for property management companies.', features: ['Unlimited properties', 'Unlimited units', 'All Expert features', 'API access', 'Dedicated support', 'SLA guarantee'] },
];

const PricingPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);
  const [currentTier, setCurrentTier] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'LANDLORD') {
      subscriptionAPI.getMyStatus().then(res => {
        if (res.data.is_active) setCurrentTier(res.data.tier);
      }).catch(() => {});
    }
  }, [isAuthenticated, user]);

  const handleSelectPlan = async (tier) => {
    if (!isAuthenticated) { navigate(`/register?plan=${tier}`); return; }
    if (user?.role !== 'LANDLORD') { toast.error('Only landlord accounts can subscribe'); return; }
    if (currentTier === tier) return;
    setLoadingTier(tier);
    try {
      const endpoint = currentTier ? '/subscriptions/change-plan' : '/subscriptions/checkout';
      const params = new URLSearchParams({ tier, origin_url: window.location.origin });
      const res = await api.post(`${endpoint}?${params}`);
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally { setLoadingTier(null); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: "'Inter', system-ui, sans-serif", color: '#1a1a2e' }} data-testid="pricing-page">
      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', boxShadow: '0 1px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <Building2 style={{ width: 28, height: 28, color: TEAL }} />
            <span style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
              Realty <span style={{ color: TEAL }}>JAG</span>
            </span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isAuthenticated ? (
              <Link to={user?.role === 'LANDLORD' ? '/landlord' : user?.role === 'SUPER_ADMIN' ? '/admin' : '/tenant'}
                style={{ fontSize: 15, fontWeight: 500, color: '#595959', textDecoration: 'none', padding: '8px 16px' }}>
                Dashboard
              </Link>
            ) : (
              <Link to="/login" style={{ fontSize: 15, fontWeight: 500, color: '#595959', textDecoration: 'none', padding: '8px 16px' }}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 48px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, color: '#1a1a2e', marginBottom: 16 }}>
          Simple, transparent <span style={{ color: TEAL }}>pricing</span>
        </h1>
        <p style={{ fontSize: 17, color: '#595959', maxWidth: 560, margin: '0 auto', marginBottom: 12 }}>
          Choose the plan that fits your portfolio. All plans include online rent collection,
          tenant portal, and maintenance tracking. Upgrade or downgrade anytime.
        </p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: `${TEAL}10`, border: `1px solid ${TEAL}30`, borderRadius: 50,
          padding: '8px 20px', fontSize: 14, fontWeight: 600, color: TEAL,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          7-day free trial on all plans — no charge until trial ends
        </div>
      </div>

      {/* Plans */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          {plans.map(plan => {
            const isCurrent = currentTier === plan.tier;
            return (
              <div
                key={plan.tier}
                data-testid={`plan-card-${plan.tier.toLowerCase()}`}
                style={{
                  position: 'relative', background: '#fff', borderRadius: 16,
                  padding: '32px 24px', display: 'flex', flexDirection: 'column',
                  border: plan.popular ? `2px solid ${TEAL}` : '1px solid #e5e7eb',
                  boxShadow: plan.popular ? `0 8px 30px ${TEAL}15` : '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                    background: TEAL, color: '#fff', fontSize: 12, fontWeight: 700,
                    padding: '4px 14px', borderRadius: 50, textTransform: 'uppercase', letterSpacing: 1,
                  }}>
                    Most Popular
                  </div>
                )}
                <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{plan.name}</h3>
                <p style={{ fontSize: 14, color: '#595959', marginBottom: 16 }}>{plan.desc}</p>
                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: 40, fontWeight: 800, color: '#1a1a2e' }}>${plan.price}</span>
                  <span style={{ fontSize: 15, color: '#595959' }}>/month</span>
                  <div style={{ fontSize: 13, color: TEAL, fontWeight: 600, marginTop: 4 }}>7 days free</div>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
                  {plan.features.map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                      <Check style={{ width: 16, height: 16, color: TEAL, marginTop: 2, flexShrink: 0 }} />
                      <span style={{ fontSize: 14, color: '#595959' }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan.tier)}
                  disabled={isCurrent || loadingTier === plan.tier}
                  data-testid={`select-plan-${plan.tier.toLowerCase()}-btn`}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 10, cursor: 'pointer',
                    fontSize: 15, fontWeight: 600, border: 'none', transition: 'opacity 0.2s',
                    background: plan.popular ? TEAL : 'transparent',
                    color: plan.popular ? '#fff' : '#1a1a2e',
                    ...(plan.popular ? {} : { border: '1.5px solid #e5e7eb' }),
                    opacity: isCurrent ? 0.5 : 1,
                  }}
                >
                  {loadingTier === plan.tier ? 'Redirecting...' : isCurrent ? 'Current Plan' : 'Start Free Trial'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
